// src/utils/filters.ts
//
// Key-driven filter helpers for the universal `StandardFilterRow`.
//
// The `FilterConfig` type lives in `@/types/filters` and is re-exported here so
// this module is the single import point for everything filter-related.
//
// A page describes its filters as a `FilterConfig[]` (each with a unique
// `key`), binds them to `StandardFilterRow`, and on `apply-filters` maps the
// returned configs into its API call by `key` (no index-based `switch`).

import type {
  ApplyFiltersPayload,
  FilterConfig,
  FilterOption,
  FilterRangeValue,
  FilterType,
  FilterValue,
} from '@/types/filters'

export type {
  ApplyFiltersPayload,
  FilterConfig,
  FilterOption,
  FilterRangeValue,
  FilterType,
  FilterValue,
}

/** Slugify a label into a stable snake_case key (`Date Range` -> `date_range`). */
export const slugifyKey = (label: string): string =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

// ---------------------------------------------------------------------------
// Filter factories
// ---------------------------------------------------------------------------
// Each factory guarantees a `key`. If no explicit key is passed, one is
// derived from the label so legacy call sites keep producing valid configs.

export const createDateFilter = (
  label = 'Date Range',
  key?: string,
): FilterConfig => ({
  key: key ?? slugifyKey(label),
  type: 'date',
  label,
  value: [null, null] as FilterRangeValue,
})

export const createSelectFilter = (
  label: string,
  options: FilterOption[],
  key?: string,
): FilterConfig => ({
  key: key ?? slugifyKey(label),
  type: 'select',
  label,
  value: [] as string[],
  options,
})

export const createNumberFilter = (
  label: string,
  prefix?: string,
  key?: string,
): FilterConfig => ({
  key: key ?? slugifyKey(label),
  type: 'number',
  label,
  value: [null, null] as FilterRangeValue,
  prefix,
})

export const createTextFilter = (
  label: string,
  key?: string,
): FilterConfig => ({
  key: key ?? slugifyKey(label),
  type: 'text',
  label,
  value: null,
})
